import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ListTodo,
  TrendingUp,
  Users
} from 'lucide-react';
import { eventService } from '../../api/taskService';
import toast from 'react-hot-toast';
import RelationshipTypeBadge from '../RelationshipTypes/RelationshipTypeBadge';
import { getRoleLabel } from '../../utils/roleLabelMapper';

/**
 * EventCard Component
 * Displays an event with progress bar, task counts, and expandable task list
 *
 * @param {Object} event - Event data object
 * @param {Function} onClick - Callback when card is clicked
 * @param {Function} onTaskClick - Callback when a task within the event is clicked
 * @param {number} index - Index for animation delay
 * @param {string} relationshipType - Relationship type for the group (default: 'nuclear_family')
 * @param {Array} participants - Array of participant objects (optional)
 */
const EventCard = ({
  event,
  onClick,
  onTaskClick,
  index = 0,
  relationshipType = 'nuclear_family',
  participants = []
}) => {
  const [expanded, setExpanded] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);

  // Format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Load event progress and tasks when expanded
  useEffect(() => {
    if (expanded && event.task_id) {
      loadEventData();
    }
  }, [expanded, event.task_id]);

  const loadEventData = async () => {
    setLoading(true);
    try {
      // Load progress and tasks in parallel
      const [progressData, tasksData] = await Promise.all([
        eventService.getEventProgress(event.task_id),
        eventService.getEventTasks(event.task_id)
      ]);

      setProgress(progressData);
      setTasks(tasksData);
    } catch (error) {
      console.error('[EventCard] Failed to load event data:', error);
      toast.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  // Calculate progress from event data or use loaded progress
  const getProgress = () => {
    if (progress) {
      return {
        total: progress.total_tasks || 0,
        completed: progress.completed_tasks || 0,
        inProgress: progress.in_progress_tasks || 0,
        percentage: progress.completion_percentage || 0
      };
    }

    // Fallback to event properties if available
    return {
      total: event.total_tasks || 0,
      completed: event.completed_tasks || 0,
      inProgress: event.in_progress_tasks || 0,
      percentage: event.completion_percentage || 0
    };
  };

  const progressData = getProgress();

  // Extract relationship-specific metadata
  const getRelationshipMetadata = () => {
    const metadata = event.relationship_metadata || {};

    // Return relevant metadata based on relationship type
    switch (relationshipType) {
      case 'event_planning':
        return metadata.event_date ? `Event Date: ${formatDate(metadata.event_date)}` : null;
      case 'group_trip':
        return metadata.destination ? `Destination: ${metadata.destination}` : null;
      case 'sports_team':
        return metadata.season ? `Season: ${metadata.season}` : null;
      case 'renovation_project':
      case 'builder_buyer':
        return metadata.property_address ? `Property: ${metadata.property_address}` : null;
      case 'performance_group':
        return metadata.performance_date ? `Performance: ${formatDate(metadata.performance_date)}` : null;
      default:
        return null;
    }
  };

  const metadataDisplay = getRelationshipMetadata();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="glass-card p-5 border-l-4 border-l-purple-500 shadow-lg hover:shadow-xl transition-shadow duration-200"
    >
      {/* Relationship Type Badge */}
      <div className="mb-3">
        <RelationshipTypeBadge relationshipType={relationshipType} size="sm" />
      </div>

      {/* Event Header */}
      <div
        className="cursor-pointer"
        onClick={() => {
          setExpanded(!expanded);
          if (onClick) onClick(event);
        }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
              {event.title}
            </h3>
            {event.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                {event.description}
              </p>
            )}
          </div>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </motion.div>
        </div>

        {/* Date Range and Metadata */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Calendar className="w-4 h-4" />
            <span>
              {formatDate(event.start_date)} - {formatDate(event.end_date)}
            </span>
          </div>

          {/* Relationship-specific metadata */}
          {metadataDisplay && (
            <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">
              {metadataDisplay}
            </div>
          )}

          {/* Participants */}
          {participants && participants.length > 0 && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <div className="flex items-center gap-1">
                {/* Avatar stack */}
                <div className="flex -space-x-2">
                  {participants.slice(0, 3).map((participant, idx) => (
                    <div
                      key={participant.user_id || idx}
                      className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-xs font-semibold"
                      title={participant.display_name || 'Member'}
                    >
                      {(participant.display_name || 'M').charAt(0).toUpperCase()}
                    </div>
                  ))}
                  {participants.length > 3 && (
                    <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-700 border-2 border-white dark:border-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-semibold">
                      +{participants.length - 3}
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  {participants.length} {participants.length === 1 ? 'member' : 'members'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Progress
            </span>
            <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
              {Math.round(progressData.percentage)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressData.percentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="bg-gradient-to-r from-purple-500 to-purple-600 h-2.5 rounded-full"
            />
          </div>
        </div>

        {/* Task Count Badges */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <ListTodo className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {progressData.total} tasks
            </span>
          </div>

          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-900 dark:text-green-300">
              {progressData.completed} completed
            </span>
          </div>

          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
              {progressData.inProgress} in progress
            </span>
          </div>
        </div>
      </div>

      {/* Expanded Task List */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
        >
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading tasks...</p>
            </div>
          ) : tasks.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Event Tasks
              </h4>
              {tasks.map((task) => (
                <motion.div
                  key={task.task_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onTaskClick) onTaskClick(task);
                  }}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className={`
                    w-2 h-2 rounded-full
                    ${task.status === 'completed' ? 'bg-green-500' :
                      task.status === 'in_progress' ? 'bg-blue-500' :
                      'bg-gray-400'}
                  `} />
                  <div className="flex-1 min-w-0">
                    <p className={`
                      text-sm font-medium
                      ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900 dark:text-white'}
                    `}>
                      {task.title === '🔒 Personal Task' ? (
                        <span className="flex items-center gap-1">
                          <span>🔒</span>
                          <span className="italic">Personal Task</span>
                        </span>
                      ) : (
                        task.title
                      )}
                    </p>
                    {task.due_date && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Due: {formatDate(task.due_date)}
                      </p>
                    )}
                  </div>
                  {task.status === 'completed' && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <ListTodo className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No tasks added to this event yet
              </p>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default EventCard;
